import { getMaterialProfile } from './materials.js';
import { calculateEffectiveThickness, calculateImpactAngle, calculateReflectionVector, normalizeVec3, } from './geometry.js';
import { calculateKineticEnergy } from './projectiles.js';
import { ChemicalEnergyPenetrationModel, StandardKineticPenetrationModel, } from './penetration.js';
export class ImpactResolver {
    kineticModel;
    chemicalModel;
    constructor(kineticModel, chemicalModel) {
        this.kineticModel = kineticModel || new StandardKineticPenetrationModel();
        this.chemicalModel = chemicalModel || new ChemicalEnergyPenetrationModel();
    }
    /**
     * Primary authoritative impact resolution function.
     *
     * @param impactPoint 3D world position where raycast intersected the hitbox
     * @param projectile Incoming shell configuration
     * @param incomingVelocity Projectile velocity vector at moment of impact
     * @param armorZone The specific hit armor zone with material, geometry, and thickness
     * @param surfaceNormal Surface outward normal vector at impact point
     */
    resolveImpact(impactPoint, projectile, incomingVelocity, armorZone, surfaceNormal) {
        const vBeforeMs = Math.sqrt(incomingVelocity.x * incomingVelocity.x +
            incomingVelocity.y * incomingVelocity.y +
            incomingVelocity.z * incomingVelocity.z);
        const incomingDir = normalizeVec3(incomingVelocity);
        const normal = normalizeVec3(surfaceNormal);
        // 1. Calculate angles: incidence angle theta & obliquity
        const { incidenceDeg, obliquityDeg, cosTheta, isBackface } = calculateImpactAngle(incomingDir, normal);
        const material = getMaterialProfile(armorZone.materialId);
        const initialEnergy = calculateKineticEnergy(projectile.massKg, vBeforeMs);
        // 2. Caliber Overmatch check (caliber > 2x nominal thickness prevents ricochet)
        const isOvermatched = projectile.caliberMm > armorZone.thicknessMm * 2.0;
        // 3. Ricochet evaluation
        // If incidence angle is greater than or equal to projectile's ricochet threshold and not overmatched
        const isRicochetAngle = incidenceDeg >= projectile.ricochetAngleDeg;
        if (isRicochetAngle && !isOvermatched && !isBackface) {
            // Geometric reflection vector
            const reflectedDir = calculateReflectionVector(incomingDir, normal);
            // Speed after ricochet based on material restitution and glancing angle
            const ricochetSpeedFactor = Math.max(0.3, material.restitution + 0.3 * (incidenceDeg / 90));
            const vAfterMs = vBeforeMs * ricochetSpeedFactor;
            const energyAfter = calculateKineticEnergy(projectile.massKg, vAfterMs);
            return {
                impactPoint: { ...impactPoint },
                armorZoneId: armorZone.armorId,
                armorZoneName: armorZone.name,
                surfaceNormal: normal,
                incomingDirection: incomingDir,
                incidenceAngleDeg: Math.round(incidenceDeg * 10) / 10,
                obliquityDeg: Math.round(obliquityDeg * 10) / 10,
                nominalThicknessMm: armorZone.thicknessMm,
                effectiveThicknessMm: Math.round(calculateEffectiveThickness(armorZone.thicknessMm, cosTheta)),
                totalLineOfSightMm: Math.round(calculateEffectiveThickness(armorZone.thicknessMm, cosTheta)),
                velocityBeforeMs: Math.round(vBeforeMs * 10) / 10,
                velocityAfterMs: Math.round(vAfterMs * 10) / 10,
                energyBeforeJoules: Math.round(initialEnergy),
                energyAfterJoules: Math.round(energyAfter),
                result: 'ricochet',
                isPenetrated: false,
                isRicochet: true,
                gameplayDamage: 0,
                spallDamage: 0,
                spallParticles: [],
                solverReason: `Ricochet: glancing impact at ${Math.round(incidenceDeg)}° exceeded ricochet threshold (${projectile.ricochetAngleDeg}°).`,
                modelConfidence: 0.96,
                deflectedDirection: reflectedDir,
                layersTraversed: [],
            };
        }
        // 4. Multi-layer / Single-layer penetration resolution
        const layers = armorZone.layers && armorZone.layers.length > 0
            ? armorZone.layers
            : [
                {
                    layerIndex: 0,
                    materialId: armorZone.materialId,
                    thicknessMm: armorZone.thicknessMm,
                    airGapMm: 0,
                },
            ];
        let currentVelocityMs = vBeforeMs;
        let totalTraversedLOSMm = 0;
        let allPenetrated = true;
        let accumulatedDamage = 0;
        let finalReason = '';
        const layersTraversed = [];
        // Select penetration model based on round type
        const model = projectile.projectileType === 'HEAT' || projectile.projectileType === 'HE'
            ? this.chemicalModel
            : this.kineticModel;
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            const layerMaterial = getMaterialProfile(layer.materialId);
            const effectiveMm = calculateEffectiveThickness(layer.thicknessMm, cosTheta);
            totalTraversedLOSMm += effectiveMm + layer.airGapMm;
            const evalInput = {
                projectile,
                impactVelocityMs: currentVelocityMs,
                incidenceAngleDeg: incidenceDeg,
                material: layerMaterial,
                nominalThicknessMm: layer.thicknessMm,
                effectiveThicknessMm: effectiveMm,
                layerIndex: i,
                isSpaced: i > 0,
            };
            const evalResult = model.evaluate(evalInput);
            layersTraversed.push({
                layerIndex: i,
                materialId: layer.materialId,
                nominalThicknessMm: layer.thicknessMm,
                pathLengthMm: Math.round(effectiveMm),
                effectiveRhaMm: Math.round(effectiveMm * layerMaterial.rhaEquivalentKinetic),
                velocityDropMs: Math.round((currentVelocityMs - evalResult.residualVelocityMs) * 10) / 10,
                penetrated: evalResult.penetrated,
            });
            if (!evalResult.penetrated) {
                allPenetrated = false;
                currentVelocityMs = 0;
                finalReason = `Stopped at layer ${i + 1} (${layerMaterial.name}): ${evalResult.reason}`;
                accumulatedDamage = evalResult.damage;
                break;
            }
            else {
                currentVelocityMs = evalResult.residualVelocityMs;
                accumulatedDamage = Math.max(accumulatedDamage, evalResult.damage);
                finalReason = evalResult.reason;
            }
        }
        const vAfterMs = currentVelocityMs;
        const energyAfter = calculateKineticEnergy(projectile.massKg, vAfterMs);
        const effectiveThicknessTotal = calculateEffectiveThickness(armorZone.thicknessMm, cosTheta);
        return {
            impactPoint: { ...impactPoint },
            armorZoneId: armorZone.armorId,
            armorZoneName: armorZone.name,
            surfaceNormal: normal,
            incomingDirection: incomingDir,
            incidenceAngleDeg: Math.round(incidenceDeg * 10) / 10,
            obliquityDeg: Math.round(obliquityDeg * 10) / 10,
            nominalThicknessMm: armorZone.thicknessMm,
            effectiveThicknessMm: Math.round(effectiveThicknessTotal),
            totalLineOfSightMm: Math.round(totalTraversedLOSMm),
            velocityBeforeMs: Math.round(vBeforeMs * 10) / 10,
            velocityAfterMs: Math.round(vAfterMs * 10) / 10,
            energyBeforeJoules: Math.round(initialEnergy),
            energyAfterJoules: Math.round(energyAfter),
            result: allPenetrated ? 'penetration' : 'stopped',
            isPenetrated: allPenetrated,
            isRicochet: false,
            gameplayDamage: accumulatedDamage,
            spallDamage: allPenetrated ? Math.round(accumulatedDamage * 0.25) : 0,
            spallParticles: [],
            solverReason: finalReason,
            modelConfidence: 0.92,
            layersTraversed,
        };
    }
}
//# sourceMappingURL=resolver.js.map